import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { BookOpen, GraduationCap, ImageOff, Clock, Globe, User, Search, Loader, Plus, Radio } from "lucide-react";
import { lmsGetCoursesByTeacher, lmsAssignCourseTeacher, lmsGetTeacherClassSubjects, lmsGetTeacherById, lmsGetTeacherByUserId, lmsGetLiveClasses } from "../api/lmsApi";
import { fetchLmsSchoolClasses, fetchLmsClassSubjects } from "../redux/lmsSlice";
import { CourseModal } from "./Courses";
import { JoinModal } from "./LiveClasses";
import "../lms.css";

const getTeacherData = () => {
  try { return JSON.parse(localStorage.getItem("userData") || "{}"); }
  catch { return {}; }
};

const LEVEL_COLORS = {
  beginner:     { bg: "#dcfce7", color: "#166534" },
  intermediate: { bg: "#fef9c3", color: "#854d0e" },
  advanced:     { bg: "#fee2e2", color: "#991b1b" },
};

export default function TeacherCourses() {
  const navigate      = useNavigate();
  const dispatch      = useDispatch();
  const userData      = getTeacherData();
  const adminId       = userData?.admin_id || null;
  const schoolId      = userData?.school_id || userData?.admin_id || null;
  const schoolClasses = useSelector((s) => s.lms?.schoolClasses || []);
  const classSubjects = useSelector((s) => s.lms?.classSubjects || []);

  // resolvedTeacherId may come from JWT directly, or be looked up via admin_id
  const [resolvedTeacherId, setResolvedTeacherId] = useState(userData?.teacher_id || null);
  const [courses,         setCourses]         = useState([]);
  const [liveClasses,     setLiveClasses]     = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [search,          setSearch]          = useState("");
  const [showModal,       setShowModal]       = useState(false);
  const [editCourse,      setEditCourse]      = useState(null);
  const [modalInitials,   setModalInitials]   = useState({});
  const [teacherClasses,  setTeacherClasses]  = useState(null);
  const [teacherSubjects, setTeacherSubjects] = useState(null);
  const [joinLiveItem,    setJoinLiveItem]    = useState(null);

  const loadCourses = (tid) => {
    if (!tid) { setLoading(false); return; }
    setLoading(true);
    lmsGetCoursesByTeacher(tid)
      .then((res) => setCourses(res?.data?.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const load = () => loadCourses(resolvedTeacherId);

  const setupTeacherData = async (tid) => {
    const [tRes, csRes] = await Promise.all([
      lmsGetTeacherById(tid).catch(() => null),
      lmsGetTeacherClassSubjects(tid).catch(() => null),
    ]);
    const initials = {};
    if (tRes?.data?.id)   initials.instructor_id   = String(tRes.data.id);
    if (tRes?.data?.name) initials.instructor_name = tRes.data.name;

    const assignments = csRes?.data?.data || [];
    if (assignments.length > 0) {
      initials.class_id   = String(assignments[0].class_id || "");
      initials.subject_id = String(assignments[0].subject_id || "");

      const seenClasses = new Set();
      const classes = [];
      assignments.forEach((a) => {
        if (!seenClasses.has(a.class_id)) {
          seenClasses.add(a.class_id);
          classes.push({ class_id: a.class_id, class_name: a.class_name });
        }
      });
      setTeacherClasses(classes);
      setTeacherSubjects(assignments.map((a) => ({
        class_id: a.class_id, subject_id: a.subject_id,
        subject_name: a.subject_name, id: a.subject_id,
      })));
    }
    setModalInitials(initials);
  };

  useEffect(() => {
    dispatch(fetchLmsSchoolClasses(schoolId));
    dispatch(fetchLmsClassSubjects({ school_id: schoolId }));

    const tid = userData?.teacher_id;
    if (tid) {
      // teacher_id already in JWT
      setResolvedTeacherId(tid);
      loadCourses(tid);
      setupTeacherData(tid);
    } else if (adminId) {
      // fallback: look up LMS teacher record by school_list user_id
      lmsGetTeacherByUserId(adminId)
        .then((res) => {
          const foundId = res?.data?.id;
          if (foundId) {
            setResolvedTeacherId(foundId);
            loadCourses(foundId);
            setupTeacherData(foundId);
          } else {
            setLoading(false);
          }
        })
        .catch(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [adminId]);

  useEffect(() => {
    const extract = (res) => res.data?.data || (Array.isArray(res.data) ? res.data : []);
    const fetchLive = async () => {
      try {
        const res = await lmsGetLiveClasses(schoolId ? { school_id: schoolId } : {});
        let data = extract(res);
        if (!data.length) {
          const res2 = await lmsGetLiveClasses({});
          data = extract(res2);
        }
        setLiveClasses(data);
      } catch {}
    };
    fetchLive();
  }, [schoolId]);

  const getActiveLiveClass = (course) => {
    const now = new Date();
    const isActive = (lc) => {
      if (lc.status === "ended") return false;
      if (lc.status === "live") return true;
      if (!lc.scheduled_at) return false;
      const start = new Date(lc.scheduled_at);
      const end = new Date(start.getTime() + (lc.duration_minutes || 60) * 60000);
      return now >= start && now <= end;
    };
    // 1. Exact class + subject match
    if (course.class_id && course.subject_id) {
      const byExact = liveClasses.find(
        (lc) => isActive(lc) &&
          String(lc.class_id) === String(course.class_id) &&
          String(lc.subject_id) === String(course.subject_id)
      );
      if (byExact) return byExact;
    }
    // 2. Same class only (no broader fallback)
    if (course.class_id) {
      const byClass = liveClasses.find(
        (lc) => isActive(lc) &&
          String(lc.class_id) === String(course.class_id) &&
          !lc.subject_id  // only match if live class has no subject restriction
      );
      if (byClass) return byClass;
    }
    return null;
  };

  const handleSaved = async (newCourseId) => {
    // Auto-assign the teacher to their newly created course
    if (newCourseId && resolvedTeacherId) {
      try {
        await lmsAssignCourseTeacher(newCourseId, { teacher_id: resolvedTeacherId, school_id: schoolId });
      } catch { /* already assigned or no teacher_id */ }
    }
    load();
  };

  const filtered = courses.filter((c) =>
    !search || c.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="crs-root">
      <div className="lms-page-header">
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <h1 className="lms-page-title">My Courses</h1>
            <span style={{ fontSize: 13, fontWeight: 700, background: "#eef2ff", color: "#4f46e5",
              padding: "3px 12px", borderRadius: 20 }}>
              {courses.length} course{courses.length !== 1 ? "s" : ""}
            </span>
          </div>
          <p className="lms-page-subtitle">Courses assigned to you</p>
        </div>
        <button className="lms-btn-primary" onClick={() => { setEditCourse(null); setShowModal(true); }}>
          <Plus size={15} /> Create Course
        </button>
      </div>

      <div className="crs-filters">
        <div className="crs-search-wrap">
          <Search size={14} />
          <input
            placeholder="Search courses..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="crs-loading"><Loader size={28} className="ms2-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="crs-empty">
          <BookOpen size={40} color="#d1d5db" />
          <p>{search ? "No courses match your search." : "No courses yet. Click \"Create Course\" to get started."}</p>
        </div>
      ) : (
        <div className="crs-grid">
          {filtered.map((c) => {
            const lvl = LEVEL_COLORS[c.level] || LEVEL_COLORS.beginner;
            const price = parseFloat(c.price) || 0;
            const discountPrice = parseFloat(c.discount_price);
            const activeLive = getActiveLiveClass(c);
            return (
              <div
                key={c.id}
                className="crs-card"
                style={{ cursor: "pointer" }}
                onClick={() => navigate(`/teacher-lms/courses/${c.id}`)}
              >
                <div className="crs-card-thumb">
                  {c.thumbnail_url ? (
                    <img src={c.thumbnail_url} alt={c.name}
                      onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }} />
                  ) : null}
                  <div className="crs-card-thumb-placeholder" style={{ display: c.thumbnail_url ? "none" : "flex" }}>
                    <ImageOff size={28} color="#9ca3af" />
                  </div>
                  <span className="crs-level-badge" style={{ background: lvl.bg, color: lvl.color }}>
                    {c.level || "beginner"}
                  </span>
                  {c.is_free ? <span className="crs-free-badge">FREE</span> : null}
                </div>

                <div className="crs-card-body">
                  <h3 className="crs-card-title">{c.name}</h3>
                  {c.description && <p className="crs-card-desc">{c.description}</p>}

                  <div className="crs-card-meta">
                    {c.instructor_name && (
                      <span className="crs-meta-item"><User size={12} />{c.instructor_name}</span>
                    )}
                    {c.duration_days && (
                      <span className="crs-meta-item"><Clock size={12} />{c.duration_days} days</span>
                    )}
                    {c.language && (
                      <span className="crs-meta-item"><Globe size={12} />{c.language}</span>
                    )}
                    {c.class_name && (
                      <span className="crs-meta-item"><GraduationCap size={12} />{c.class_name}</span>
                    )}
                  </div>

                  <div className="crs-card-price" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div>
                      {c.is_free ? (
                        <span className="crs-price-free">Free</span>
                      ) : discountPrice ? (
                        <>
                          <span className="crs-price-discounted">₹{discountPrice.toFixed(2)}</span>
                          <span className="crs-price-original">₹{price.toFixed(2)}</span>
                        </>
                      ) : (
                        <span className="crs-price-main">{price > 0 ? `₹${price.toFixed(2)}` : "Free"}</span>
                      )}
                    </div>
                    {activeLive && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setJoinLiveItem(activeLive); }}
                        style={{
                          display: "inline-flex", alignItems: "center", gap: 5,
                          padding: "6px 12px", borderRadius: 8, border: "none",
                          background: "#16a34a", color: "#fff",
                          fontWeight: 700, fontSize: 12, cursor: "pointer",
                        }}
                      >
                        <Radio size={13} /> Join Live
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {joinLiveItem && (
        <JoinModal liveClass={joinLiveItem} onClose={() => setJoinLiveItem(null)} teacherMode />
      )}

      {showModal && (
        <CourseModal
          course={editCourse}
          schoolId={schoolId}
          classes={(teacherClasses && teacherClasses.length > 0) ? teacherClasses : schoolClasses}
          classSubjects={(teacherSubjects && teacherSubjects.length > 0) ? teacherSubjects : classSubjects}
          onClose={() => { setShowModal(false); setEditCourse(null); }}
          onSaved={handleSaved}
          initialValues={editCourse ? undefined : modalInitials}
        />
      )}
    </div>
  );
}
